/**
 * Keys and values must be the same to allow for the these entries to pass conditional equality checks with UResourceType.
 */
export enum EResourceType {
  image = 'image',
  // ? I'm still not sure this really belongs here, so far as it will never show up in the same "resources.type" slots in the entity/part/skill config files
  pixiManifest = 'pixiManifest',
  spritesheet = 'spritesheet',
  multiAtlas = 'multiAtlas',
  spineAtlas = 'spineAtlas',
  // TODO: Remove "track" from the name, it's not a track, it's a sound file.
  audioTrack = 'audioTrack'
}

/**
 * Used for config file structure comparisons.
 * TODO: I'd like to not need this, and that will be the case if/when all configs use keys like `[EResourceType.image]:...` instead of strings.
 */
export type UResourceType = keyof typeof EResourceType;

/**
 * These should fall under the "state/usage" object of a resource (given a EResourceType). They aren't resourcees themselves.
 */
export enum EResourceStateKey {
  spineSkin = 'spineSkin',
  animationClip = 'animationClip'
}

/**
 * Used for config file structure comparisons.
 */
export function isResourceType(str: string): str is EResourceType {
  return str in EResourceType;
}

/**
 * This represents the broad/wider structure of the image config object, which it must conform to.
 * The actual config object is imported and validated against this structure.
 */
export type AssetConfigImages = Record<string, string>;

// Spine atlases

// TODO: remove ISpineCustomSkinData
// TODO: remove ISpineDataset
export type SpineDataset = {
  path: string;
  json: string;
  atlas: string;
};

/**
 * This represents the broad/wider structure of the spine config object, which it must conform to.
 * The actual config object is imported and validated against this structure.
 */
export type AssetConfigSpine = Record<string, SpineDataset>;

// Pixi manifest

export interface IUnresolvedAsset {
  alias?: string[] | string;
  src?: string[] | string;
  data?: any;
}

export interface IAssetsBundle {
  name: string;
  assets: IUnresolvedAsset[] | Record<string, string[] | string | IUnresolvedAsset>;
}

/**
 * This represents the broad/wider structure of the pixi manifest object, which it must conform to.
 * The actual config object is imported and validated against this structure.
 */
export type AssetManifestPixi = {
  bundles: IAssetsBundle[];
};

// Atlas/Spritesheet structures

interface IAnimClipFrameData {
  frameRate?: number;
  repeat: number;
}

interface IAnimClipNumberedFrameData extends IAnimClipFrameData {
  start: number;
  end: number;
  first?: number;
}

interface IAnimClipNamedFrameData extends IAnimClipNumberedFrameData {
  prefix: string;
}

// SpriteSheets - unused right now
export interface ISsDataset {
  path: string;
  frameData: {
    frameWidth: number;
    frameHeight: number;
  };
  clips?: { [clipName: string]: IAnimClipNumberedFrameData };
  frames?: { [frameName: string]: string };
}

// Sprite atlases - unused right now
interface ISaDataset {
  path: string;
  img: string;
  json: string;
  frameData?: IAnimClipFrameData;
  frames?: { [frameName: string]: string };
}

// Multi atlases
export interface IMaDataset {
  path: string;
  json: string;
  clips?: { [clipName: string]: IAnimClipNamedFrameData };
  frames?: { [frameName: string]: string };
}

export type AssetConfigSpriteSheet = Record<string, ISsDataset>;

/**
 * This represents the broad/wider structure of the multi-atlas config object, which it must conform to.
 * The actual config object is imported and validated against this structure.
 */
export type AssetConfigMultiAtlas = Record<string, IMaDataset>;

// Audio

// TODO: Woops, make into enum. Not urgent.
export type AudioTypes = 'sfx' | 'music';

export type AudioDataset = {
  path: string;
  type: AudioTypes;
  data?: {
    loop?: boolean;
    volume?: number;
    speed?: number;
  };
};

/**
 * This represents the broad/wider structure of the audio config object, which it must conform to.
 * The actual config object is imported and validated against this structure.
 */
export type AssetConfigAudio = Record<string, AudioDataset>;
